import { Button, EmptyState, Field, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  fetchOrganization,
  type OrganizationSettings,
  PROVIDER_NAMES,
  type ProviderName,
  updateOrganization,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useCan } from '../lib/session'

/**
 * Organisation settings (M3-03): the name, the provider a shipment gets when the call names none,
 * which providers may be routed to at all, and how long data is kept.
 *
 * A viewer and a developer read this page; only `organization:update` may save, so the form is
 * rendered read-only for everyone else rather than hidden — knowing the retention term is useful
 * even to someone who may not change it.
 */
export const Route = createFileRoute('/_app/settings/organization')({ component: Organization })

function Organization() {
  const { t } = useConsoleI18n()
  const can = useCan()
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; organization: OrganizationSettings }
    | { status: 'error'; error: ApiError }
  >({ status: 'loading' })
  const [error, setError] = useState<string | undefined>(undefined)
  const [saved, setSaved] = useState(false)

  const load = useCallback(async () => {
    try {
      setState({ status: 'ready', organization: (await fetchOrganization()).organization })
    } catch (cause) {
      setState({ status: 'error', error: asApiError(cause) })
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (state.status === 'loading') {
    return (
      <div className="flex justify-center py-10">
        <Spinner label={t('loading')} size="lg" />
      </div>
    )
  }
  if (state.status === 'error') {
    return <EmptyState title={t('error.title')} description={t(state.error.messageKey)} />
  }

  return (
    <OrganizationForm
      organization={state.organization}
      editable={can('organization:update')}
      error={error}
      saved={saved}
      onSave={async (patch) => {
        setError(undefined)
        setSaved(false)
        try {
          const { organization } = await updateOrganization(patch)
          setState({ status: 'ready', organization })
          setSaved(true)
        } catch (cause) {
          const failure = asApiError(cause)
          setError(failure.problem?.detail ?? t(failure.messageKey))
        }
      }}
    />
  )
}

interface OrganizationPatch {
  name: string
  default_provider: ProviderName
  providers_enabled: readonly ProviderName[]
  retention_days: number
}

function OrganizationForm({
  organization,
  editable,
  error,
  saved,
  onSave,
}: {
  readonly organization: OrganizationSettings
  readonly editable: boolean
  readonly error: string | undefined
  readonly saved: boolean
  readonly onSave: (patch: OrganizationPatch) => Promise<void>
}) {
  const { t } = useConsoleI18n()
  const [name, setName] = useState(organization.name)
  const [provider, setProvider] = useState<ProviderName>(organization.default_provider)
  const [enabled, setEnabled] = useState<readonly ProviderName[]>(organization.providers_enabled)
  const [retention, setRetention] = useState(String(organization.retention_days))
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    await onSave({
      name: name.trim(),
      default_provider: provider,
      providers_enabled: enabled,
      retention_days: Number(retention),
    })
    setBusy(false)
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={(event) => void onSubmit(event)}>
      <div className="flex flex-col gap-2">
        <h2 className="font-semibold text-lead text-ink">{t('organization.title')}</h2>
        <p className="text-body text-muted">{t('organization.intro')}</p>
      </div>

      {error === undefined ? null : (
        <p role="alert" className="text-body text-danger">
          {error}
        </p>
      )}
      {saved ? (
        <p role="status" className="text-body text-muted">
          {t('organization.saved')}
        </p>
      ) : null}

      <Field label={t('organization.name.label')} required>
        {({ id, describedBy, invalid }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            type="text"
            required
            maxLength={120}
            disabled={!editable}
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="min-h-touch w-full rounded-card border border-line bg-surface px-3 text-body text-ink disabled:opacity-50"
          />
        )}
      </Field>

      <Field
        label={t('organization.default_provider.label')}
        hint={t('organization.default_provider.hint')}
      >
        {({ id, describedBy }) => (
          <select
            id={id}
            aria-describedby={describedBy}
            disabled={!editable}
            value={provider}
            onChange={(event) => setProvider(event.target.value as ProviderName)}
            className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink disabled:opacity-50"
          >
            {PROVIDER_NAMES.map((name) => (
              // A provider that may not be routed to cannot be the default; the API agrees.
              <option key={name} value={name} disabled={!enabled.includes(name)}>
                {name}
              </option>
            ))}
          </select>
        )}
      </Field>

      <fieldset className="flex flex-col gap-2 border-0 p-0">
        <legend className="font-medium text-body text-ink">
          {t('organization.providers.label')}
        </legend>
        <p className="text-caption text-muted">{t('organization.providers.hint')}</p>
        <div className="flex flex-wrap gap-3">
          {PROVIDER_NAMES.map((value) => (
            <label key={value} className="flex items-center gap-2 text-body text-muted">
              <input
                type="checkbox"
                disabled={!editable}
                checked={enabled.includes(value)}
                onChange={(event) =>
                  setEnabled((current) =>
                    event.target.checked
                      ? [...current, value]
                      : current.filter((entry) => entry !== value),
                  )
                }
              />
              <span className="font-mono text-caption">{value}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <Field label={t('organization.retention.label')} hint={t('organization.retention.hint')}>
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            type="number"
            min={30}
            max={3650}
            step={1}
            disabled={!editable}
            value={retention}
            onChange={(event) => setRetention(event.target.value)}
            className="min-h-touch w-32 rounded-card border border-line bg-surface px-3 text-body text-ink disabled:opacity-50"
          />
        )}
      </Field>

      {editable ? (
        <div>
          <Button
            type="submit"
            busy={busy}
            busyLabel={t('loading')}
            disabled={enabled.length === 0}
          >
            {t('organization.save')}
          </Button>
        </div>
      ) : null}
    </form>
  )
}

function asApiError(cause: unknown): ApiError {
  return cause instanceof ApiError
    ? cause
    : new ApiError({ status: 0, code: 'network_error', messageKey: 'error.network' })
}
